import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Button, Modal, Input, InputNumber, Spin, Empty, Switch } from "antd";
import { toast } from "../../components/toasts/toastStore";
import { apiProveedoresInstance, authHeaderProveedores } from "../../redux/actions/proveedores/proveedores";
import { SaveOutlined, CloseOutlined } from "@ant-design/icons";

import "./EventoDetallePage.css";

dayjs.locale("es");

const fmtFechaCorta = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("D MMM YYYY, HH:mm") : "—";
};

const fmtDatetime = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD/MM/YYYY, HH:mm") : "—";
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const ACTION_CONFIG = {
  CREATE: { label: "Creacion", color: "#15803d", bg: "#dcfce7" },
  UPDATE: { label: "Actualizacion", color: "#f90336", bg: "#fee2e6" },
  DELETE: { label: "Eliminacion", color: "#b91c1c", bg: "#fee2e2" },
};
const getActionConfig = (a) => ACTION_CONFIG[a] || { label: a || "Cambio", color: "#595c5e", bg: "#f1f5f9" };

export default function ProveedorDetallePage() {
  const { idProveedor } = useParams();
  const navigate = useNavigate();

  const [proveedor, setProveedor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("datos");

  const [editingInfo, setEditingInfo] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  const [editForm, setEditForm] = useState({});

  const [compras, setCompras] = useState([]);
  const [encargos, setEncargos] = useState([]);
  const [retiros, setRetiros] = useState([]);
  const [loadingRel, setLoadingRel] = useState(false);

  const [actividad, setActividad] = useState([]);
  const [loadingActividad, setLoadingActividad] = useState(false);

  const fetchProveedor = useCallback(() => {
    setLoading(true);
    apiProveedoresInstance.get(`/proveedores/${idProveedor}`, { headers: authHeaderProveedores() })
      .then((res) => setProveedor(res.data))
      .catch(() => toast.error("No se pudo cargar el proveedor"))
      .finally(() => setLoading(false));
  }, [idProveedor]);

  useEffect(() => { fetchProveedor(); }, [fetchProveedor]);

  useEffect(() => {
    if (!["compras", "encargos", "retiros"].includes(activeTab)) return;
    setLoadingRel(true);
    apiProveedoresInstance.get(`/proveedores/${idProveedor}/${activeTab}`, { headers: authHeaderProveedores() })
      .then((res) => {
        if (activeTab === "compras") setCompras(res.data || []);
        if (activeTab === "encargos") setEncargos(res.data || []);
        if (activeTab === "retiros") setRetiros(res.data || []);
      })
      .catch(() => {})
      .finally(() => setLoadingRel(false));
  }, [activeTab, idProveedor]);

  useEffect(() => {
    if (activeTab !== "actividad") return;
    setLoadingActividad(true);
    apiProveedoresInstance.get(`/proveedores/${idProveedor}/actividad`, { headers: authHeaderProveedores() })
      .then((res) => setActividad(res.data || []))
      .catch(() => {})
      .finally(() => setLoadingActividad(false));
  }, [activeTab, idProveedor]);

  const startEditInfo = () => {
    setEditForm({
      clave_proveedor: proveedor.clave_proveedor,
      nombre: proveedor.nombre,
      linea_surte: proveedor.linea_surte,
      telefono: proveedor.telefono,
      contacto: proveedor.contacto,
      rfc: proveedor.rfc,
      direccion: proveedor.direccion,
      da_credito: Boolean(proveedor.da_credito),
      plazo_dias_credito: proveedor.plazo_dias_credito,
      nota: proveedor.nota,
    });
    setEditingInfo(true);
  };

  const handleSaveInfo = async () => {
    if (!editForm.nombre?.trim()) return toast.warning("El nombre es obligatorio");
    setSavingInfo(true);
    try {
      await apiProveedoresInstance.patch(`/proveedores/${idProveedor}`, editForm, { headers: authHeaderProveedores() });
      toast.success("Proveedor actualizado correctamente");
      setEditingInfo(false);
      fetchProveedor();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSavingInfo(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Eliminar este proveedor?",
      content: "Esta accion desactiva el proveedor. No se puede deshacer.",
      okText: "Eliminar",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        setDeleting(true);
        try {
          await apiProveedoresInstance.delete(`/proveedores/${idProveedor}`, { headers: authHeaderProveedores() });
          toast.success("Proveedor eliminado");
          navigate("/app/proveedores");
        } catch (err) {
          toast.error(err?.response?.data?.detail || "No se pudo eliminar");
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="cd-main" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!proveedor) {
    return (
      <div className="cd-main" style={{ padding: 40 }}>
        <Empty description="Proveedor no encontrado" />
      </div>
    );
  }

  return (
    <div className="cd-main">
      <div className="cd-content">
        <div className="cd-header-card">
          <div className="cd-header-top">
            <div>
              <div className="cd-header-name-row">
                <h1 className="cd-client-name">{(proveedor.nombre || "—").toUpperCase()}</h1>
              </div>
              <div className="cd-header-meta">
                <span className="cd-meta-item"><i className="fa-solid fa-calendar" /> Creado el {fmtFechaCorta(proveedor.datetime)}</span>
                {proveedor.creado_por && <span className="cd-meta-item"><i className="fa-solid fa-user" /> {capitalize(proveedor.creado_por)}</span>}
              </div>
            </div>
            <div className="cd-header-actions">
              <Button icon={<i className="fa-solid fa-trash" />} className="cd-btn-delete" loading={deleting} onClick={handleDelete}>
                Eliminar
              </Button>
            </div>
          </div>

          <div className="cd-header-tabs">
            <button className={`cd-tab-btn ${activeTab === "datos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("datos")}>
              <i className="fa-solid fa-file-lines" /> Informacion
            </button>
            <button className={`cd-tab-btn ${activeTab === "compras" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("compras")}>
              <i className="fa-solid fa-cart-arrow-down" /> Compras
            </button>
            <button className={`cd-tab-btn ${activeTab === "encargos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("encargos")}>
              <i className="fa-solid fa-box-archive" /> Encargos
            </button>
            <button className={`cd-tab-btn ${activeTab === "retiros" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("retiros")}>
              <i className="fa-solid fa-money-bill-transfer" /> Retiros de caja
            </button>
            <button className={`cd-tab-btn ${activeTab === "actividad" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("actividad")}>
              <i className="fa-solid fa-clock" /> Actividad
            </button>
          </div>
        </div>

        {activeTab === "datos" && (
          <div className="cd-card cd-card-client">
            <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-truck-field"></i></div>
                <h2 className="cd-card-title">Informacion del proveedor</h2>
              </div>
              {!editingInfo && (
                <Button icon={<i className="fa-solid fa-pen" />} className="cd-btn-edit" onClick={startEditInfo}>Editar</Button>
              )}
            </div>

            {editingInfo ? (
              <div className="cd-edit-form">
                <div className="cd-edit-grid">
                  <div className="cd-edit-field"><span className="cd-edit-label">Nombre</span><Input value={editForm.nombre} onChange={(e) => setEditForm((f) => ({ ...f, nombre: e.target.value }))} /></div>
                  <div className="cd-edit-field"><span className="cd-edit-label">Clave</span><Input value={editForm.clave_proveedor || ""} onChange={(e) => setEditForm((f) => ({ ...f, clave_proveedor: e.target.value }))} /></div>
                  <div className="cd-edit-field"><span className="cd-edit-label">Linea que surte</span><Input value={editForm.linea_surte || ""} onChange={(e) => setEditForm((f) => ({ ...f, linea_surte: e.target.value }))} /></div>
                  <div className="cd-edit-field"><span className="cd-edit-label">Contacto</span><Input value={editForm.contacto || ""} onChange={(e) => setEditForm((f) => ({ ...f, contacto: e.target.value }))} /></div>
                  <div className="cd-edit-field"><span className="cd-edit-label">Telefono</span><Input value={editForm.telefono || ""} onChange={(e) => setEditForm((f) => ({ ...f, telefono: e.target.value }))} /></div>
                  <div className="cd-edit-field"><span className="cd-edit-label">RFC</span><Input value={editForm.rfc || ""} onChange={(e) => setEditForm((f) => ({ ...f, rfc: e.target.value }))} /></div>
                  <div className="cd-edit-field" style={{ gridColumn: "1 / -1" }}><span className="cd-edit-label">Direccion</span><Input value={editForm.direccion || ""} onChange={(e) => setEditForm((f) => ({ ...f, direccion: e.target.value }))} /></div>
                  <div className="cd-edit-field"><span className="cd-edit-label">Da credito</span><Switch checked={editForm.da_credito} onChange={(v) => setEditForm((f) => ({ ...f, da_credito: v }))} /></div>
                  {editForm.da_credito && (
                    <div className="cd-edit-field"><span className="cd-edit-label">Plazo de credito (dias)</span><InputNumber style={{ width: "100%" }} min={0} value={editForm.plazo_dias_credito} onChange={(v) => setEditForm((f) => ({ ...f, plazo_dias_credito: v }))} /></div>
                  )}
                  <div className="cd-edit-field" style={{ gridColumn: "1 / -1" }}><span className="cd-edit-label">Nota</span><Input.TextArea rows={3} value={editForm.nota || ""} onChange={(e) => setEditForm((f) => ({ ...f, nota: e.target.value }))} /></div>
                </div>
                <div className="cd-edit-actions">
                  <Button icon={<CloseOutlined />} onClick={() => setEditingInfo(false)} disabled={savingInfo}>Cancelar</Button>
                  <Button type="primary" icon={<SaveOutlined />} loading={savingInfo} onClick={handleSaveInfo} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>Guardar</Button>
                </div>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 32px" }}>
                <div><span className="cd-field-label">Nombre</span><span className="cd-field-value">{capitalize(proveedor.nombre)}</span></div>
                <div><span className="cd-field-label">Clave</span><span className="cd-field-value">{proveedor.clave_proveedor || "—"}</span></div>
                <div><span className="cd-field-label">Linea que surte</span><span className="cd-field-value">{proveedor.linea_surte || "—"}</span></div>
                <div><span className="cd-field-label">Contacto</span><span className="cd-field-value">{proveedor.contacto || "—"}</span></div>
                <div><span className="cd-field-label">Telefono</span><span className="cd-field-value">{proveedor.telefono || "—"}</span></div>
                <div><span className="cd-field-label">RFC</span><span className="cd-field-value">{proveedor.rfc || "—"}</span></div>
                <div style={{ gridColumn: "1 / -1" }}><span className="cd-field-label">Direccion</span><span className="cd-field-value">{proveedor.direccion || "—"}</span></div>
                <div>
                  <span className="cd-field-label">Credito</span>
                  <span className="cd-field-value" style={{ color: proveedor.da_credito ? "#15803d" : "#b45309" }}>
                    {proveedor.da_credito ? `Si, a ${proveedor.plazo_dias_credito ?? "?"} dias` : "No otorga credito"}
                  </span>
                </div>
                <div><span className="cd-field-label">Fecha de creacion</span><span className="cd-field-value">{fmtFechaCorta(proveedor.datetime)}</span></div>
                {proveedor.nota && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <span className="cd-field-label">Nota</span>
                    <span className="cd-field-value" style={{ fontWeight: 400, color: "#595c5e", lineHeight: 1.5 }}>{proveedor.nota}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === "compras" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-cart-arrow-down"></i></div>
              <h2 className="cd-card-title">Compras a este proveedor</h2>
            </div>
            {loadingRel ? <Spin /> : compras.length === 0 ? (
              <Empty description="Todavia no hay compras registradas a este proveedor" />
            ) : (
              <div className="cd-pagos-list">
                {compras.map((c) => (
                  <div key={c.id_compra} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, padding: "12px 4px", borderBottom: "1px solid #f0edf2" }}>
                    <div><span className="cd-field-label">Folio factura</span><div className="cd-field-value">{c.folio_factura || "—"}</div></div>
                    <div><span className="cd-field-label">Total</span><div className="cd-field-value">{money(c.total)}</div></div>
                    <div><span className="cd-field-label">Estatus</span><div className="cd-field-value">{c.estatus_nombre || "—"}</div></div>
                    <div><span className="cd-field-label">Fecha</span><div className="cd-field-value">{fmtDatetime(c.datetime)}</div></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "encargos" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-box-archive"></i></div>
              <h2 className="cd-card-title">Encargos a este proveedor</h2>
            </div>
            {loadingRel ? <Spin /> : encargos.length === 0 ? (
              <Empty description="Todavia no hay encargos registrados a este proveedor" />
            ) : (
              <div className="cd-pagos-list">
                {encargos.map((e) => (
                  <div key={e.id_encargo} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, padding: "12px 4px", borderBottom: "1px solid #f0edf2" }}>
                    <div><span className="cd-field-label">Descripcion</span><div className="cd-field-value">{e.descripcion}</div></div>
                    <div><span className="cd-field-label">Cliente</span><div className="cd-field-value">{e.cliente_nombre || "—"}</div></div>
                    <div><span className="cd-field-label">Estado</span><div className="cd-field-value">{e.estado_nombre || "—"}</div></div>
                    <div><span className="cd-field-label">Fecha</span><div className="cd-field-value">{fmtDatetime(e.datetime)}</div></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "retiros" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-money-bill-transfer"></i></div>
              <h2 className="cd-card-title">Retiros de caja pagados a este proveedor</h2>
            </div>
            {loadingRel ? <Spin /> : retiros.length === 0 ? (
              <Empty description="Todavia no hay retiros de caja registrados para este proveedor" />
            ) : (
              <div className="cd-pagos-list">
                {retiros.map((r) => (
                  <div key={r.id_retiro} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, padding: "12px 4px", borderBottom: "1px solid #f0edf2" }}>
                    <div><span className="cd-field-label">Motivo</span><div className="cd-field-value">{r.motivo_nombre || "—"}</div></div>
                    <div><span className="cd-field-label">Monto</span><div className="cd-field-value">{money(r.monto)}</div></div>
                    <div><span className="cd-field-label">Fecha</span><div className="cd-field-value">{fmtDatetime(r.datetime)}</div></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "actividad" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-clock"></i></div>
              <h2 className="cd-card-title">Actividad</h2>
              <span className="cd-actividad-count">{actividad.length}</span>
            </div>
            {loadingActividad ? <Spin /> : (
              <div className="cd-actividad-list">
                {actividad.map((item, idx) => {
                  const cfg = getActionConfig(item.action);
                  return (
                    <div key={item.id_audit_log || idx} className="cd-actividad-item">
                      <div className="cd-actividad-dot-col">
                        <div className="cd-actividad-dot" style={{ background: cfg.color }} />
                        {idx < actividad.length - 1 && <div className="cd-actividad-line" />}
                      </div>
                      <div className="cd-actividad-body">
                        <div className="cd-actividad-top-row">
                          <span className="cd-actividad-badge" style={{ color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
                          <span className="cd-actividad-message">{item.message}</span>
                        </div>
                        <div className="cd-actividad-bottom-row">
                          <span className="cd-actividad-time">{fmtDatetime(item.datetime)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
